import { useState, useCallback, useRef } from 'react';

export type SavingStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseSavingIndicatorReturn {
  status: SavingStatus;
  error: Error | null;
  trackMutation: <T>(promise: Promise<T>) => Promise<T>;
  reset: () => void;
}

/**
 * Hook to track mutation status for quick action saving indicators.
 * Shows "saved" for 2 seconds after success, auto-clears error after 5 seconds.
 */
export function useSavingIndicator(): UseSavingIndicatorReturn {
  const [status, setStatus] = useState<SavingStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimeouts();
    setStatus('idle');
    setError(null);
  }, [clearTimeouts]);

  const trackMutation = useCallback(async <T>(promise: Promise<T>): Promise<T> => {
    clearTimeouts();
    setStatus('saving');
    setError(null);

    try {
      const result = await promise;
      setStatus('saved');
      
      // Clear "saved" status after 2 seconds
      timeoutRef.current = setTimeout(() => {
        setStatus('idle');
      }, 2000);
      
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setStatus('error');
      setError(error);
      
      // Auto-clear error after 5 seconds
      errorTimeoutRef.current = setTimeout(() => {
        setStatus('idle');
        setError(null);
      }, 5000);
      
      throw error;
    }
  }, [clearTimeouts]);

  return { status, error, trackMutation, reset };
}
